import React, { useMemo, useState, useEffect } from 'react';
import { Grid, Typography, Box, Button, Select, MenuItem, FormControl } from '@mui/material';
import CalendarHeatmap from './CalendarHeatmap';
import { dailyOccupancyDataConst } from 'src/utils/const';
import { useUpgradeMesageExpertFeature } from 'src/hooks/useUpgradeText';
import { COLOR_PRIMARY_2 } from 'src/utils/colors';
import { User } from 'src/interfaces/iuser';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCachedMarketData } from 'src/store/Dashboard/actions';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import OccupancyLineGraph from 'src/App/Dashboards/Market/Pages/OverviewPage/UtilityComponents/OccupancyLineGraph';
import { useCanAccessExpertPlan } from 'src/hooks/useFeatureAccess';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import InfoButton from 'src/App/Dashboards/Market/Components/Buttons/InfoButton';

interface YearMonth {
  year: number;
  month: number;
}

interface Props {
  user: User;
  marketDisplayName: string;
  handleOnSampleClick: (plan: string) => void;
  isMobileScreen: boolean;
  isDemoMarket: boolean;
  warningTextComponent: any;
  dailyOccupancyRateCalendarHistoricalGraphRef: any;
  dailyOccupancyRateCalendarFutureGraphRef: any;
  setSelectedGraphExportDetails: any;
  marketId: string;
  /** When true, show calendar data/loading without requiring "Reveal" click (e.g. in Market Intelligence). */
  autoReveal?: boolean;
}

const DailyOccupancyRateCalendar: React.FC<Props> = ({
  user,
  marketDisplayName,
  handleOnSampleClick,
  isMobileScreen,
  isDemoMarket,
  warningTextComponent,
  dailyOccupancyRateCalendarHistoricalGraphRef,
  dailyOccupancyRateCalendarFutureGraphRef,
  setSelectedGraphExportDetails,
  marketId,
  autoReveal = false,
}) => {
  const dispatch = useDispatch();
  const [isRevealed, setIsRevealed] = useState(Boolean(autoReveal));
  const [selectedYear, setSelectedYear] = useState<number>(2025);
  const cachedMarketData = useSelector((state: any) => state.dashboard.cachedMarketData);
  const searchData = useSelector((state: any) => state.dashboard?.search?.data);
  const userCountryCode = useSelector((state: any) => state.dashboard?.userCountryCode);
  const upgradeMessage = useUpgradeMesageExpertFeature();
  const isNotPremiumUser = !useCanAccessExpertPlan();

  const { historicalData, isLoading } = useMemo(() => {
    if (!isRevealed) {
      return {
        historicalData: dailyOccupancyDataConst as Record<string, number>,
        isLoading: false,
      };
    }
    const raw =
      cachedMarketData?.data?.historical_occupancy_calendar as
        | Record<string, { occupancy?: number }>
        | undefined
        | null;
    const transformed = Object.entries(raw ?? {}).reduce(
      (acc, [date, info]) => {
        acc[date] = typeof info?.occupancy === 'number' ? info.occupancy : 0;
        return acc;
      },
      {} as Record<string, number>
    );
    return {
      historicalData: transformed,
      isLoading: cachedMarketData?.loading ?? true,
    };
  }, [cachedMarketData?.data?.historical_occupancy_calendar, cachedMarketData?.loading, isRevealed]);

  const handleReveal = () => {
    if (isNotPremiumUser) {
      handleOnSampleClick('expert');
      return;
    }
    setIsRevealed(true);
  };

  const handleRefresh = () => {
    if (!searchData) return;
    dispatch(
      fetchCachedMarketData(
        marketId,
        searchData.name,
        searchData.city,
        searchData.countryCode,
        searchData.country,
        userCountryCode ?? '',
        true,
        searchData.isVerifiedMarket ?? false
      )
    );
  };

  const uniqueYearMonths: YearMonth[] = useMemo(() => {
    const seen = new Set<string>();
    const result: YearMonth[] = [];
    Object.keys(historicalData).forEach(dateStr => {
      const [yearStr, monthStr] = dateStr.split('-');
      const year = +yearStr;
      const month = +monthStr - 1;
      const key = `${year}-${month}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ year, month });
      }
    });
    result.sort((a, b) => (a.year === b.year ? a.month - b.month : a.year - b.year));
    return result;
  }, [historicalData]);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    uniqueYearMonths.forEach(({ year }) => years.add(year));
    return Array.from(years).sort((a, b) => b - a);
  }, [uniqueYearMonths]);

  useEffect(() => {
    if (availableYears.length > 0) {
      setSelectedYear(prev =>
        availableYears.includes(prev)
          ? prev
          : availableYears.includes(2025)
            ? 2025
            : availableYears[0]
      );
    }
  }, [availableYears]);

  const filteredYearMonths = useMemo(() => {
    return uniqueYearMonths.filter(({ year }) => year === selectedYear);
  }, [uniqueYearMonths, selectedYear]);

  const getLineGraphData = (data: Record<string, number>, year?: number) => {
    return Object.entries(data)
      .filter(([date]) => {
        if (!year) return true;
        return new Date(date).getFullYear() === year;
      })
      .map(([date, occupancy]) => ({ date, occupancy, isPrelim: false }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const historicalLineGraphData = getLineGraphData(
    historicalData,
    isRevealed ? selectedYear : undefined
  );

  const renderMonthTitle = (year: number, month: number) => {
    const currentMonthDate = new Date(year, month, 1);
    const monthName = currentMonthDate.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });
    return (
      <Typography variant="body1" className="subHeadings">
        {monthName}
      </Typography>
    );
  };

  const hasData = Object.keys(historicalData).length > 0;
  const showRefresh =
    isRevealed &&
    !isLoading &&
    hasData &&
    searchData?.id === marketId;

  return (
    <>
      <Grid
        container
        sx={{
          p: 2,
          boxShadow: '0 4px 10px #0003',
          borderRadius: 1,
          position: 'relative',
        }}
      >
        {showRefresh && <RefreshButton handleRefresh={handleRefresh} />}

        {warningTextComponent && warningTextComponent}

        <Grid container justifyContent="space-between" alignItems="center">
          <Typography
            variant="body1"
            className="subHeadings"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Historical Daily Occupancy Calendar of {marketDisplayName}
            <InfoButton
              title=""
              tooltipContent="Daily Calendar View is only available for markets, not for submarkets."
            />
            {isRevealed && hasData && cachedMarketData?.data?.historical_occupancy_calendar && (
              <IconButton
                color="primary"
                onClick={() => {
                  const raw = cachedMarketData.data.historical_occupancy_calendar as Record<
                    string,
                    { occupancy?: number }
                  >;
                  const exportableGraphData: any = Object.entries(raw).map(
                    ([date, occupancyInfo]: [string, any]) => [
                      date,
                      (occupancyInfo?.occupancy ?? 0) + '%',
                      'Historical',
                    ]
                  );
                  exportableGraphData.sort(
                    (a: any, b: any) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
                  );
                  exportableGraphData.unshift(['Date', 'Occupancy', 'Type']);
                  setSelectedGraphExportDetails({
                    graphData: exportableGraphData,
                    fieldName: `daily_occupancy_rate_calendar_historical.csv`,
                    graphType: 'eventsAndHolidaysGraphs',
                  });
                }}
              >
                <DownloadRoundedIcon />
              </IconButton>
            )}
          </Typography>
          {isRevealed && availableYears.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedYear}
                onChange={e => setSelectedYear(e.target.value as number)}
                sx={{ height: 36 }}
              >
                {availableYears.map(year => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>

        <Grid
          container
          justifyContent="center"
          alignItems="flex-start"
          flexWrap="wrap"
          sx={{
            gap: 4,
            mt: 2,
            filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
            pointerEvents: !isRevealed ? 'none' : 'auto',
          }}
        >
          {isRevealed && isLoading && !hasData && (
            <LoadingCard height={150} type="bars" noShadow={true} />
          )}

          {(hasData || !isRevealed) &&
            (isRevealed ? filteredYearMonths : uniqueYearMonths).map(({ year, month }) => (
              <Grid item key={`${year}-${month}`}>
                <CalendarHeatmap
                  year={year}
                  month={month}
                  occupancyData={historicalData}
                  monthTitle={renderMonthTitle(year, month)}
                  calendarType="historical"
                />
              </Grid>
            ))}

          {isRevealed && isLoading && !hasData && (
            <Grid container justifyContent="center" sx={{ py: 2 }}>
              <Typography color="textSecondary">Loading historical occupancy data...</Typography>
            </Grid>
          )}
        </Grid>

        {(hasData || !isRevealed) && !isLoading && (
          <Box
            sx={{
              mt: 4,
              width: '100%',
              filter: !isRevealed ? 'blur(4px) brightness(1.1)' : 'none',
            }}
          >
            <OccupancyLineGraph
              data={historicalLineGraphData}
              graphRef={dailyOccupancyRateCalendarHistoricalGraphRef}
            />
          </Box>
        )}

        {!isRevealed && (
          <ActionButton
            isNotPremiumUser={isNotPremiumUser}
            isMobileScreen={isMobileScreen}
            handleReveal={handleReveal}
            upgradeMessage={upgradeMessage}
          />
        )}
      </Grid>
    </>
  );
};

const ActionButton = ({
  isNotPremiumUser,
  isMobileScreen,
  handleReveal,
  upgradeMessage,
}: {
  isNotPremiumUser: boolean;
  isMobileScreen: boolean;
  handleReveal: () => void;
  upgradeMessage: string;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1,
        textAlign: 'center',
      }}
    >
      {isNotPremiumUser ? (
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobileScreen ? 18 : 22,
            fontWeight: 600,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            textDecoration: 'underline',
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: '#fffe',
          }}
          onClick={handleReveal}
        >
          {upgradeMessage}
        </Typography>
      ) : (
        <Button
          variant="contained"
          onClick={handleReveal}
          sx={{
            backgroundColor: COLOR_PRIMARY_2,
            color: '#fff',
            boxShadow: '0 4px 10px #0003',
            fontWeight: 600,
            fontSize: isMobileScreen ? 18 : 22,
            px: 2,
            py: 1,
            transition: 'transform 0.15s, box-shadow 0.15s',
            '&:hover': {
              backgroundColor: COLOR_PRIMARY_2,
              opacity: 0.9,
              color: '#fff',
            },
          }}
        >
          Reveal Data
        </Button>
      )}
    </Box>
  );
};

const RefreshButton = ({ handleRefresh }: { handleRefresh: () => void }) => {
  return (
    <Tooltip title="Refresh to get the latest data">
      <IconButton
        onClick={handleRefresh}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 3,
          background: '#fff',
          boxShadow: '0 2px 8px #0002',
          '&:hover': { background: '#f5f5f5' },
        }}
        size="small"
      >
        <RefreshIcon color="primary" />
      </IconButton>
    </Tooltip>
  );
};
export default React.memo(DailyOccupancyRateCalendar);
